import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import contractService from '../../../services/contractService';
import toast from 'react-hot-toast';
import ContractDetailModal from './ContractDetailModal';

const ContractManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [visibleColumns, setVisibleColumns] = useState({
    contractTitle: true,
    subject: true,
    pjoName: true,
    status: true,
    dateTimeCreated: true,
    action: true
  });

  const [contractsData, setContractsData] = useState([]);
  const [selectedContract] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateFile, setTemplateFile] = useState(null);

  const fetchContracts = useCallback(async () => {
    try {
      // Build query params for backend API
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      }

      const response = await contractService.listAll(params.toString());
      if (response.success) {
        setContractsData(response.data.contracts || []);
        // Update pagination state if backend returns it
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (err) {
      toast.error('Failed to fetch contracts');
    }
  }, [currentPage, itemsPerPage, searchTerm, sortConfig]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Sorting logic - now handled by backend
  const sortedContracts = useMemo(() => {
    // Backend handles sorting, just return contracts as-is
    return contractsData;
  }, [contractsData]);

  const filteredContracts = useMemo(() => {
    // Backend handles search, just return contracts as-is
    return sortedContracts;
  }, [sortedContracts]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to page 1 on search
    // fetchContracts will be called automatically by useEffect
  };

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const exportToExcel = async () => {
    try {
      const blob = await contractService.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracts_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Excel report downloaded');
    } catch (err) {
      toast.error('Failed to download Excel report');
    } finally {
      setShowExportMenu(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["#", "Contract title", "Subject", "PJO Name", "Status", "Date/Time Created"];
    const tableRows = filteredContracts.map((item, index) => [
      index + 1,
      item.contract_name || 'N/A',
      item.subject || 'N/A',
      item.contractPeople?.map(cp => cp.user?.first_name).join(', ') || 'N/A',
      item.status?.toUpperCase() || 'N/A',
      new Date(item.created_at).toLocaleString()
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [51, 51, 51] }
    });
    doc.text("contract", 14, 15);
    doc.save("contract.pdf");
    setShowExportMenu(false);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeletedContract, setIsDeletedContract] = useState(false);

  const handleDeleteClick = (id, isDeleted = false) => {
    setDeleteId(id);
    setIsDeletedContract(isDeleted);
    setShowDeleteModal(true);
  };

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreId, setRestoreId] = useState(null);

  const confirmDelete = async () => {
    try {
      if (isDeletedContract) {
        // Permanent delete
        await contractService.permanentDelete(deleteId);
        toast.success('Contract permanently deleted');
      } else {
        // Soft delete
        await contractService.delete(deleteId);
        toast.success('Contract soft-deleted successfully');
      }
      fetchContracts();
    } catch (err) {
      toast.error(`Failed to delete contract: ${err.message}`);
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
      setIsDeletedContract(false);
    }
  };

  const handleRestoreClick = (id) => {
    setRestoreId(id);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    try {
      await contractService.restore(restoreId);
      toast.success('Contract restored successfully');
      fetchContracts();
    } catch (err) {
      toast.error('Failed to restore contract');
    } finally {
      setShowRestoreModal(false);
      setRestoreId(null);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await contractService.download(id);
      if (response.success && response.data.fileUrl) {
        window.open(response.data.fileUrl, '_blank');
      }
    } catch (err) {
      toast.error('Failed to download contract');
    }
  };

  const handleRemind = async (id, email) => {
    try {
      const response = await contractService.remind(id, email);
      if (response.success) {
        toast.success('Reminder sent successfully');
      }
    } catch (err) {
      toast.error('Failed to send reminder');
    }
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/contract/${id}/edit`);
  };

  const handleUploadTemplate = () => {
    setShowUploadModal(true);
  };

  const resetUploadForm = () => {
    setTemplateTitle('');
    setTemplateFile(null);
  };

  const closeUploadModal = () => {
    if (uploadLoading) return;
    setShowUploadModal(false);
    resetUploadForm();
  };

  const loadHelloSignSdk = () => {
    if (window.HelloSign) {
      return Promise.resolve(window.HelloSign);
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-hellosign-sdk="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.HelloSign), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load HelloSign SDK')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.hellosign.com/public/js/embedded/v2.11.1/embedded.production.min.js';
      script.async = true;
      script.setAttribute('data-hellosign-sdk', 'true');
      script.onload = () => resolve(window.HelloSign);
      script.onerror = () => reject(new Error('Failed to load HelloSign SDK'));
      document.body.appendChild(script);
    });
  };

  const openTemplateEditor = async (editUrl) => {
    const clientId = process.env.REACT_APP_HELLOSIGN_CLIENT_ID;
    if (!clientId) {
      throw new Error('REACT_APP_HELLOSIGN_CLIENT_ID is missing in frontend .env');
    }

    const HelloSignSdk = await loadHelloSignSdk();
    if (!HelloSignSdk) {
      throw new Error('HelloSign SDK unavailable');
    }
    const openOptions = {
      skipDomainVerification: process.env.NODE_ENV !== 'production',
      testMode: 1,
      allowCancel: true,
    };

    // Support both SDK styles:
    // 1) Static: HelloSign.open(url, { clientId, ... })
    // 2) Instance: new HelloSign({ clientId }).open(url, {...})
    if (typeof HelloSignSdk.open === 'function') {
      HelloSignSdk.open(editUrl, {
        clientId,
        ...openOptions,
      });
      return;
    }

    if (typeof HelloSignSdk === 'function') {
      const client = new HelloSignSdk({ clientId });
      if (typeof client.open !== 'function') {
        throw new Error('HelloSign SDK loaded but open() is unavailable');
      }
      client.open(editUrl, openOptions);
      return;
    }

    throw new Error('Unsupported HelloSign SDK shape');
  };

  const submitTemplateUpload = async () => {
    if (!templateFile) {
      toast.error('Please select a PDF file');
      return;
    }
    if (!templateTitle.trim()) {
      toast.error('Template title is required');
      return;
    }

    const formData = new FormData();
    formData.append('template_file', templateFile);
    formData.append('title', templateTitle.trim());

    setUploadLoading(true);
    try {
      const response = await contractService.uploadTemplate(formData);
      const editUrl = response?.data?.editUrl || '';

      if (!editUrl) {
        throw new Error('HelloSign editor URL not returned');
      }

      await openTemplateEditor(editUrl);
      // Close local upload modal once HelloSign editor launches.
      setShowUploadModal(false);
      resetUploadForm();
      toast.success('Template draft created. HelloSign editor opened.');
    } catch (err) {
      toast.error(err?.message || 'Failed to upload contract template');
    } finally {
      setUploadLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const [activeTooltip, setActiveTooltip] = useState(null);

  const Tooltip = ({ text, id }) => (
    <div
      className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1a1a1a] text-white text-[12px] rounded shadow-xl whitespace-nowrap z-[100] transition-opacity duration-200 pointer-events-none ${activeTooltip === id ? 'opacity-100' : 'opacity-0'}`}
      style={{ borderBottom: '1px solid transparent' }}
    >
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-[#1a1a1a]"></div>
    </div>
  );

  return (
    <div className="p-0 bg-white min-h-screen font-sans relative">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded shadow-2xl w-[450px] max-w-[95%] overflow-hidden transform transition-all animate-in fade-in zoom-in duration-200">
            {/* Top Border Accent */}
            <div className="h-1.5 bg-[#f05033]"></div>

            <div className="p-8 max-sm:p-6 flex flex-col items-center text-center">
              {/* Warning Icon */}
              <div className="mb-4">
                <div className="relative flex items-center justify-center">
                  <i className="fas fa-triangle-exclamation text-[#f05033] text-7xl max-sm:text-5xl"></i>
                </div>
              </div>

              <h2 className="text-2xl max-sm:text-xl font-bold text-gray-800 mb-2">Are you sure?</h2>

              <p className="text-gray-600 text-[15px] leading-relaxed mb-8 px-4 max-sm:px-2 font-medium">
                {isDeletedContract 
                  ? 'You are about to PERMANENTLY DELETE this Contract. This action cannot be undone. Click "DELETE" to proceed or "CANCEL" to exit.'
                  : 'You are about to DELETE this Contract. Click "DELETE" to proceed or "CANCEL" to exit.'}
              </p>

              <div className="flex items-center justify-center gap-3 w-full max-sm:flex-col">
                <button
                  onClick={confirmDelete}
                  className="px-8 py-2.5 bg-[#f05033] text-white font-bold rounded shadow-sm hover:bg-[#d63d22] transition-colors min-w-[120px] max-sm:w-full"
                >
                  DELETE
                </button>
                <button
                  onClick={cancelDelete}
                  className="px-8 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded shadow-sm hover:bg-gray-50 transition-colors min-w-[120px] max-sm:w-full"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded shadow-2xl w-[450px] max-w-[95%] overflow-hidden transform transition-all animate-in fade-in zoom-in duration-200">
            <div className="h-1.5 bg-[#4caf50]"></div>
            <div className="p-8 max-sm:p-6 flex flex-col items-center text-center">
              <div className="mb-4">
                <i className="fas fa-undo text-[#4caf50] text-7xl max-sm:text-5xl"></i>
              </div>
              <h2 className="text-2xl max-sm:text-xl font-bold text-gray-800 mb-2">Restore Contract?</h2>
              <p className="text-gray-600 text-[15px] leading-relaxed mb-8 px-4 max-sm:px-2 font-medium">
                You are about to RESTORE this Contract. Click "RESTORE" to proceed or "CANCEL" to exit.
              </p>
              <div className="flex items-center justify-center gap-3 w-full max-sm:flex-col">
                <button
                  onClick={confirmRestore}
                  className="px-8 py-2.5 bg-[#4caf50] text-white font-bold rounded shadow-sm hover:bg-[#43a047] transition-colors min-w-[120px] max-sm:w-full"
                >
                  RESTORE
                </button>
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="px-8 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded shadow-sm hover:bg-gray-50 transition-colors min-w-[120px] max-sm:w-full"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 z-[120] bg-black bg-opacity-50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded shadow-2xl w-full max-w-2xl overflow-hidden mx-auto mt-16 mb-8">
            <div className="h-1.5 bg-[#ffeb00]"></div>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Upload Contract Template</h2>
              <button
                onClick={closeUploadModal}
                className="h-8 w-8 rounded hover:bg-gray-100 text-gray-500"
                aria-label="Close upload modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-gray-700">Template Title *</label>
                  <input
                    type="text"
                    value={templateTitle}
                    onChange={(e) => setTemplateTitle(e.target.value)}
                    placeholder="e.g. Charter Agreement V1"
                    className="px-3 py-2 border border-gray-300 rounded text-sm focus:border-[#ffeb00] focus:outline-none"
                    disabled={uploadLoading}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-gray-700">PDF File *</label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                    disabled={uploadLoading}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={closeUploadModal}
                  className="px-5 py-2 text-sm font-bold text-gray-700 bg-[#e9ecef] hover:bg-gray-200 rounded border border-gray-200"
                  disabled={uploadLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={submitTemplateUpload}
                  className={`px-5 py-2 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-sm ${uploadLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={uploadLoading}
                >
                  {uploadLoading ? 'Uploading...' : 'Upload & Open Editor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedContract && (
        <ContractDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          data={selectedContract}
          onRemind={handleRemind}
          onDownload={handleDownload}
        />
      )}

      {/* breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 font-medium px-6 py-4">
        <i className="fas fa-file-contract"></i>
        <span>Contracts</span>
      </div>

      <div className="px-6 pb-6">
        <div className="bg-white rounded shadow-sm border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-white px-6 border-b border-gray-100 flex items-center justify-between max-sm:px-4 max-sm:flex-col max-sm:items-start max-sm:gap-3 max-sm:py-3">
            <h1 className="text-lg font-bold text-gray-800">Contract Management</h1>
            <div className="flex items-center gap-3 max-sm:flex-wrap">
              {/* Export Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#ffeb00] text-black font-bold text-sm rounded shadow-sm hover:bg-[#ffd200] transition-colors min-w-[100px] justify-between"
                >
                  <div className="flex items-center gap-2">
                    <i className="fas fa-file-export"></i>
                    <span>Export</span>
                  </div>
                  <i className={`fas fa-chevron-${showExportMenu ? 'up' : 'down'} text-[10px]`}></i>
                </button>
                {showExportMenu && (
                  <div
                    className="absolute left-0 mt-1 w-full bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded shadow-lg z-50 overflow-hidden"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-2.5 text-sm text-black flex items-center gap-2 transition-colors border-b border-gray-50 font-medium"
                    >
                      <i className="fas fa-file-excel text-black-600"></i>
                      Excel
                    </button>
                    <button
                      onClick={exportToPDF}
                      className="w-full text-left px-4 py-2.5 text-sm text-black flex items-center gap-2 transition-colors font-medium"
                    >
                      <i className="fas fa-file-pdf text-black-600"></i>
                      PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Hide/Show Column Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#ffeb00] text-black font-bold text-sm rounded shadow-sm hover:bg-[#ffd200] transition-colors min-w-[160px] justify-between"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span>Hide/Show Column</span>
                  </div>
                  <i className={`fas fa-chevron-${showColumnMenu ? 'up' : 'down'} text-[10px]`}></i>
                </button>
                {showColumnMenu && (
                  <div
                    className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-50 py-2"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {Object.keys(visibleColumns).map((col) => {
                      const labels = {
                        contractTitle: 'Contract Title',
                        subject: 'Subject',
                        pjoName: 'PJO Name',
                        status: 'Status',
                        dateTimeCreated: 'Date/Time Created'
                      };
                      if (col === 'action') return null;
                      return (
                        <div key={col} className="px-4 py-1.5 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                          <label className="flex items-center gap-3 cursor-pointer w-full">
                            <div className="relative flex items-center">
                              <input
                                type="checkbox"
                                checked={visibleColumns[col]}
                                onChange={() => toggleColumn(col)}
                                className="w-4 h-4 accent-[#ffd200]"
                              />
                              <i className="fas fa-check absolute scale-0 peer-checked:scale-100 text-[#ffeb00] text-[10px] left-[3px] transition-transform"></i>
                            </div>
                            <span className="text-xs font-medium text-gray-700 select-none">{labels[col]}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                onClick={handleUploadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-[#ffeb00] text-black font-bold text-sm rounded shadow-sm hover:bg-[#ffd200] transition-colors"
              >
                <i className="fas fa-upload"></i>
                <span>Upload Contract Template</span>
              </button>

              <button
                onClick={() => navigate('/admin/contract/create')}
                className="flex items-center gap-2 px-4 py-2 bg-[#ffeb00] text-black font-bold text-sm rounded shadow-sm hover:bg-[#ffd200] transition-colors"
              >
                <i className="fas fa-plus"></i>
                <span>Send Contract</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                <span>Display</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded focus:border-[#ffeb00] focus:outline-none bg-white font-bold"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>Records</span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Search</span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="px-3 py-1.5 border border-gray-300 rounded focus:border-[#ffeb00] focus:outline-none w-64 text-sm font-medium shadow-sm transition-all"
                  />
                </div>

                <div className="flex items-center border border-gray-200 rounded overflow-hidden shadow-sm">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={!pagination.hasPrevPage}
                    className={`px-3 py-1.5 text-xs font-bold ${
                      pagination.hasPrevPage 
                        ? 'text-gray-700 hover:bg-gray-100 cursor-pointer' 
                        : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    } transition-colors`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-xs font-bold ${
                        currentPage === page
                          ? 'bg-[#ffeb00] text-black'
                          : 'text-gray-500 hover:bg-gray-100'
                      } transition-colors`}
                    >
                      {page}
                    </button>
                  ))}
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                    disabled={!pagination.hasNextPage}
                    className={`px-3 py-1.5 text-xs font-bold ${
                      pagination.hasNextPage 
                        ? 'text-gray-700 hover:bg-gray-100 cursor-pointer' 
                        : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    } transition-colors`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-sm overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-white border-b border-gray-200">
                    <th
                      className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors w-16 border-r border-gray-100"
                      onClick={() => requestSort('id')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span>#</span>
                        <i className="fas fa-sort text-gray-300 text-xs"></i>
                      </div>
                    </th>
                    {visibleColumns.contractTitle && (
                      <th
                        className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
                        onClick={() => requestSort('contractTitle')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>Contract Title</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                    {visibleColumns.subject && (
                      <th
                        className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
                        onClick={() => requestSort('subject')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>Subject</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                    {visibleColumns.pjoName && (
                      <th
                        className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
                        onClick={() => requestSort('pjoName')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>Airliner</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                    {visibleColumns.status && (
                      <th
                        className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100"
                        onClick={() => requestSort('status')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>Status</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                    {visibleColumns.dateTimeCreated && (
                      <th
                        className="px-4 py-4 text-sm font-bold text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors border-r border-gray-100 text-center"
                        onClick={() => requestSort('dateTimeCreated')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span className="whitespace-nowrap">Date/Time Created</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                    {visibleColumns.action && (
                      <th className="px-4 py-4 text-sm font-bold text-gray-800">
                        <div className="flex items-center justify-center gap-2">
                          <span>Action</span>
                          <i className="fas fa-sort text-gray-300 text-xs"></i>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic">
                        No contracts found matching your search.
                      </td>
                    </tr>
                  ) : (
                    filteredContracts.map((contract, index) => (
                      <tr key={contract.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm text-gray-800 border-r border-gray-100 text-center font-bold">
                          {contract.id}
                        </td>
                        {visibleColumns.contractTitle && (
                          <td className="px-4 py-4 text-sm text-gray-600 border-r border-gray-100 uppercase text-xs text-center font-medium">
                            {contract.contract_name}
                          </td>
                        )}
                        {visibleColumns.subject && (
                          <td className="px-4 py-4 text-sm text-gray-600 border-r border-gray-100 text-center text-xs font-medium">
                            {contract.subject}
                          </td>
                        )}
                        {visibleColumns.pjoName && (
                          <td className="px-4 py-4 text-sm text-gray-600 border-r border-gray-100 text-center text-sm font-bold">
                            {contract.airliner_name || contract.signer?.user ? `${contract.signer.user.first_name} ${contract.signer.user.last_name}` : 'N/A'}
                          </td>
                        )}
                        {visibleColumns.status && (
                          <td className="px-4 py-4 text-sm border-r border-gray-100 text-center">
                            <span className={`inline-block px-3 py-1 text-white text-[11px] font-bold rounded-sm shadow-sm tracking-wide ${
                              contract.status === 'Deleted' ? 'bg-[#dd4b39]' : 
                              contract.status === 'Completed' ? 'bg-[#00a65a]' : 
                              contract.status === 'Sent' ? 'bg-[#00c0ef]' :
                              contract.status === 'Viewed' ? 'bg-[#3c8dbc]' :
                              contract.status === 'Started' ? 'bg-[#605ca8]' :
                              contract.status === 'Declined' ? 'bg-[#dd4b39]' :
                              contract.status === 'Voided' ? 'bg-[#999999]' :
                              'bg-[#f39c12]' // Draft status
                            }`}>
                              {contract.status?.toUpperCase() || 'N/A'}
                            </span>
                          </td>
                        )}
                        {visibleColumns.dateTimeCreated && (
                          <td className="px-4 py-4 text-sm text-gray-600 border-r border-gray-100 text-center text-xs font-medium">
                            {new Date(contract.created_at).toLocaleString()}
                          </td>
                        )}
                        {visibleColumns.action && (
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              {contract.status === 'Deleted' ? (
                                <>
                                  {/* Restore Button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => handleRestoreClick(contract.id)}
                                      onMouseEnter={() => setActiveTooltip(`restore-${contract.id}`)}
                                      onMouseLeave={() => setActiveTooltip(null)}
                                      className="h-8 w-8 flex items-center justify-center rounded bg-[#3c8dbc] text-white hover:bg-[#367fa9] transition-colors shadow-sm"
                                    >
                                      <i className="fas fa-undo text-sm"></i>
                                    </button>
                                    <Tooltip text="Restore Contract" id={`restore-${contract.id}`} />
                                  </div>

                                  {/* Permanent Delete Button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => handleDeleteClick(contract.id, true)}
                                      onMouseEnter={() => setActiveTooltip(`perm-delete-${contract.id}`)}
                                      onMouseLeave={() => setActiveTooltip(null)}
                                      className="h-8 w-8 flex items-center justify-center rounded bg-[#dd4b39] text-white hover:bg-[#c23321] transition-colors shadow-sm"
                                    >
                                      <i className="fas fa-trash text-sm"></i>
                                    </button>
                                    <Tooltip text="Permanent Delete" id={`perm-delete-${contract.id}`} />
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* View Button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => handleViewDetails(contract.id)}
                                      onMouseEnter={() => setActiveTooltip(`view-${contract.id}`)}
                                      onMouseLeave={() => setActiveTooltip(null)}
                                      className="h-8 w-8 flex items-center justify-center rounded bg-[#ffeb00] text-black hover:bg-[#ffd200] transition-colors shadow-sm"
                                    >
                                      <i className="fas fa-eye text-sm"></i>
                                    </button>
                                    <Tooltip text="View signature status & details" id={`view-${contract.id}`} />
                                  </div>

                                  {/* Delete Button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => handleDeleteClick(contract.id, false)}
                                      onMouseEnter={() => setActiveTooltip(`delete-${contract.id}`)}
                                      onMouseLeave={() => setActiveTooltip(null)}
                                      className="h-8 w-8 flex items-center justify-center rounded bg-[#dd4b39] text-white hover:bg-[#c23321] transition-colors shadow-sm"
                                    >
                                      <i className="fas fa-trash text-sm"></i>
                                    </button>
                                    <Tooltip text="Delete Contract" id={`delete-${contract.id}`} />
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm text-gray-600 font-medium">
              <div>
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total} entries
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractManagement;
